import React, { useEffect } from "react";

import { Label } from "@busbud/horizon";
import type * as UDM from "@busbud/int-schemas/udm";

import type { SeatSelectionDetailsMode } from "@app/constants/seat-selection-details-modes";
import { useAppContext } from "@app/helpers/hooks";
import { seatSelectionHasGenders } from "@app/modules/checkout/utils/bus";
import { checkoutErrorPopup } from "@app/tracking/checkout-tracking";
import type { Gender } from "@app/types/checkout-types";
import type { CurrencyCode } from "@app/types/currency";
import type { NapiDepartureCart } from "@app/types/napi-departures-types";
import type { SearchInfo } from "@app/types/search-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import type { SeatPrices } from "../seat-price";
import type {
  Direction,
  SelectedSeatsInfo
} from "../seat-selector/seat-selector";

import { LoadingBusLayout } from "../loading-bus-layout";
import OperatorDisclaimers from "../operator-disclaimers";
import { SeatSelector } from "../seat-selector";
import { SeatSelectorWithGender } from "../seat-selector-with-gender";

export interface Props {
  contractor_name: string | null;
  currency: CurrencyCode;
  is_external: boolean;
  mode: SeatSelectionDetailsMode;
  operator_disclaimers: string[] | null;
  operator_name: string;
  prices: SeatPrices;
  search: SearchInfo;
  trip_id: string;
  vehicle: UDM.Bus | null;
  is_vehicle_loading_complete: boolean;
  departure_time: string;
  segment_id: string;
  initial_passengers?: PassengerSeatSelectionInstance[];
  initial_selected_seats?: SelectedSeatsInfo;
  is_checkout?: boolean;
  passenger_index?: number;
  direction?: Direction;
  onGenderSelect?: (gender: Gender, passenger_index: number) => void;
  onConfirm?: (
    passengers: PassengerSeatSelectionInstance[],
    trip_id: string
  ) => void;
}

const ErrorMessage: React.FC<{
  operator_name: string;
}> = ({ operator_name }) => {
  const { translator, tracker } = useAppContext();

  useEffect(() => {
    tracker.track(
      checkoutErrorPopup(
        null,
        "unavailable_seat_selection",
        null,
        tracker.analytics
      )
    );
  }, []);

  return (
    <Label
      size="lg"
      className="p-200 text-center text-color-secondary"
      as="div"
    >
      {translator.t("!checkout.seat-selection.unavailable-seat-selection", {
        operator: operator_name
      })}
    </Label>
  );
};

export const SeatSelectionContent = (props: Props) => {
  const {
    vehicle,
    contractor_name,
    operator_disclaimers,
    operator_name,
    is_external,
    mode,
    currency,
    search,
    is_vehicle_loading_complete,
    trip_id,
    prices,
    departure_time,
    segment_id,
    initial_passengers,
    initial_selected_seats,
    is_checkout,
    passenger_index,
    direction = "outbound",
    onGenderSelect,
    onConfirm
  } = props;

  if (!is_vehicle_loading_complete && vehicle?.layout === undefined) {
    return <LoadingBusLayout />;
  }

  // Bus layout found
  if (vehicle && Array.isArray(vehicle.layout)) {
    const has_gendered_seats = seatSelectionHasGenders([
      {
        vehicle,
        id: segment_id
      }
    ] as unknown as Pick<NapiDepartureCart, "vehicle" | "id">[]);

    const SeatSelectorComponent = has_gendered_seats
      ? SeatSelectorWithGender
      : SeatSelector;

    return (
      <>
        <OperatorDisclaimers
          className="p-200"
          contractor_name={contractor_name}
          operator_disclaimers={operator_disclaimers}
        />
        <SeatSelectorComponent
          onConfirm={onConfirm}
          is_external={is_external}
          currency={currency}
          search={search}
          mode={mode}
          direction={direction}
          trip_id={trip_id}
          vehicle={vehicle}
          prices={prices}
          departure_time={departure_time}
          segment_id={segment_id}
          initial_passengers={initial_passengers}
          initial_selected_seats={initial_selected_seats}
          is_checkout={is_checkout}
          passenger_index={passenger_index}
          onGenderSelect={onGenderSelect}
        />
      </>
    );
  }
  return <ErrorMessage operator_name={operator_name} />;
};
